import { useState, useCallback } from 'react';
import { getDeviceTokens, registerDeviceToken as registerToken } from '@/services/edgeFunctionWrappers/notificationsManagementWrapper';
import type { DeviceToken } from '@/services/edgeFunctionWrappers/notificationsManagementWrapper';

export type { DeviceToken };

export function useDeviceTokens() {
  const [tokens, setTokens] = useState<DeviceToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Fetch existing tokens for the logged in user */
  const fetchTokens = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await getDeviceTokens();

      if (queryError) throw queryError;
      setTokens(data || []);
    } catch (err: any) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /** 
   * Register a new device token for the logged in user
   * @param deviceToken - The FCM device token
   * @param userId - The user ID (for logging purposes)
   * @param appName - The app name for multi-tenant isolation
   */
  const registerDeviceToken = useCallback(async (deviceToken: string, userId: string, appName?: string) => {
    setError(null);
    try {
      const { error: rpcError } = await registerToken(deviceToken, appName);

      if (rpcError) throw rpcError;

      // Refresh list
      await fetchTokens();
    } catch (err: any) {
      console.error(err);
      setError(err.message);
    }
  }, [fetchTokens]);

  return {
    tokens,
    loading,
    error,
    fetchTokens,
    registerToken: registerDeviceToken
  };
}
